import {
  getAllNotificationsURL,
  getAllUnreadNotificationsCount,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from "apis/notification";
import { get, post } from "utils/request";
import { toast } from "react-toastify";
import { useRouter } from "components/hooks";
import { useQuery } from "@tanstack/react-query";
import { handleRedirectOrPopupActionForNotification } from "utils/HelperFunctions";
import Loading from "components/atoms/Loading";
import { useLoaderContext } from "components/context/LoaderContext";
import { useBrandContext } from "components/context/BrandContext";
import { useUserContext } from "components/context/UserContext";
import useNotificationStore from "globalstore/notificationStore";
import { Helmet } from "react-helmet-async";
import { TOAST_CONFIG } from "../../constants";
import Notification from "./Notification";

function NotificationPage() {
  const router = useRouter();
  const { revalidateBrandDetails } = useBrandContext();
  const { reloadUserData, setCurrentlyViewingMementoId } = useUserContext();
  const { setLoading } = useLoaderContext();
  const handleGetAllUnreadNotificationCountError = (errorMessage: String) => {
    toast.error(errorMessage, TOAST_CONFIG);
  };

  const { notifications: notificationsFromFirebase } = useNotificationStore();

  const {
    isLoading: isUnreadNotificationsCountUnread,
    data: unreadNotificationData,
    error: unreadNotificationCountError,
    refetch: refetchUnreadNotificationCount,
  } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ["unread-notifications-count"],
    queryFn: () =>
      get(getAllUnreadNotificationsCount(), {
        processError: handleGetAllUnreadNotificationCountError,
      }),
  });

  const handleGetAllNotificationsError = () => {
    toast.error("Failed to load new notifications", TOAST_CONFIG);
  };

  const {
    isLoading: isNotificationsLoading,
    data: allNotificationsData,
    error: notificationsErrors,
    refetch: refetchAllNotificationsData,
  } = useQuery({
    refetchInterval: 5000,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ["notifications"],
    queryFn: () =>
      get(getAllNotificationsURL(), {
        queryParams: {
          page_size: "100",
        },
        processError: handleGetAllNotificationsError,
      }),
  });

  const triggerRevalidation = () => {
    refetchAllNotificationsData();
    refetchUnreadNotificationCount();
  };

  const handleMarkAllNotificationAsReadSuccess = () => {
    triggerRevalidation();
    setLoading(false);
  };

  const handleMarkAllNotificationAsReadError = (errorMessage: string) => {
    toast.error(errorMessage, TOAST_CONFIG);
    setLoading(false);
  };

  const handleMarkAllNotificationsAsRead = async () => {
    setLoading(true);
    post(
      markAllNotificationsAsRead(),
      {},
      {
        processData: handleMarkAllNotificationAsReadSuccess,
        processError: handleMarkAllNotificationAsReadError,
      },
    );
  };

  const handleMarkNotificationAsReadAndNavigateToCta = async (notification: NotificationData) => {
    post(
      markNotificationAsRead(notification.id),
      {},
      {
        processData: () => {
          const isBrandReloadNotification = notification.cta?.includes("reloadBrand");
          const isUserReloadNotification = notification.cta?.includes("reloadUser");
          if (isBrandReloadNotification) {
            revalidateBrandDetails();
          }
          if (isUserReloadNotification) {
            reloadUserData();
          }
          triggerRevalidation();

          const navigationLink = handleRedirectOrPopupActionForNotification(notification, setCurrentlyViewingMementoId);
          if (navigationLink) router.go(navigationLink);
        },
      },
    );
  };

  if (isNotificationsLoading || isUnreadNotificationsCountUnread) return <Loading />;

  if (notificationsErrors || unreadNotificationCountError) return <p>Something went wrong!</p>;

  const unReadFirebaseNotificationCount = notificationsFromFirebase.reduce(
    (acc, currentValue) => acc + (currentValue.is_read ? 0 : 1),
    0,
  );

  return (
    <>
      <Helmet>
        <title> Notifications - MAGNETIQ</title>
      </Helmet>
      <Notification
        unreadNotificationsCount={(unreadNotificationData?.unread_count || 0) + unReadFirebaseNotificationCount}
        notifications={[...notificationsFromFirebase, ...(allNotificationsData?.results || [])]}
        handleMarkAllNotificationsAsRead={handleMarkAllNotificationsAsRead}
        handleMarkNotificationAsReadAndNavigateToCta={handleMarkNotificationAsReadAndNavigateToCta}
      />
    </>
  );
}
export default NotificationPage;
